import type { ExperienceRole } from '@portfolio/shared';

/**
 * Career history exactly as approved. Dates, employers and responsibilities
 * must not be altered; client names are deliberately absent.
 */
export const experienceRoles: readonly ExperienceRole[] = [
  {
    id: 'ibm',
    company: 'IBM',
    role: 'Senior Full Stack Developer',
    location: 'Bucharest, Romania (remote)',
    period: { start: '2025-05', end: '2026-07', label: 'May 2025 - July 2026' },
    summary:
      'Frontend-focused role on an enterprise AI-driven code transformation platform that ' +
      'migrated applications between programming languages while preserving business logic.',
    highlights: [
      'Designed and implemented workflows for configuring, executing and monitoring AI-assisted code transformations.',
      'Integrated React and Next.js interfaces with Python/FastAPI orchestration services for code parsing, LLM execution, validation and reporting.',
      'Built source-code ingestion, project configuration, migration job creation, transformation review, validation results and generated-code analysis interfaces.',
      'Managed asynchronous workflows including progress, errors, retries and validation failures.',
      'Presented token usage, confidence scores and migration progress in dashboards.',
      'Contributed to frontend architecture, coding standards and component design, and built reusable accessible UI components.',
      'Optimized rendering, state management, data fetching, frontend error handling and keyboard navigation.',
      'Surfaced execution metadata from MCP, Snowflake and Databricks pipelines.',
    ],
    technologies: [
      'React',
      'Next.js',
      'TypeScript',
      'JavaScript',
      'REST APIs',
      'Python',
      'FastAPI',
      'LLM workflows',
      'MCP servers',
      'Snowflake',
      'Databricks',
      'PostgreSQL',
    ],
    engagements: [],
  },
  {
    id: 'arobs',
    company: 'AROBS Transilvania Software',
    role: 'Senior Frontend Developer',
    location: 'Iasi, Romania',
    period: { start: '2014-10', end: '2025-05', label: 'October 2014 - May 2025' },
    summary:
      'Ten years of frontend delivery across four long-running products in public safety, ' +
      'healthcare, hospitality SaaS and contract management.',
    highlights: [],
    technologies: [
      'React',
      'Next.js',
      'Vue.js',
      'TypeScript',
      'RxJS',
      'Apollo Client',
      'GraphQL',
      'Storybook',
      'Node.js',
      'NestJS',
      'Kafka',
      'RabbitMQ',
      'Azure',
      'Docker',
    ],
    engagements: [
      {
        id: 'disaster-alert',
        title: 'Disaster Alert System - Public Safety Platform',
        period: { start: '2022-12', end: '2025-05', label: 'December 2022 - May 2025' },
        market: 'USA',
        summary:
          'Nationwide emergency alert platform handling high-volume real-time event streams.',
        highlights: [
          'Led React and Vue.js interface development for a nationwide emergency alert platform.',
          'Architected reusable TypeScript component libraries for alert-management workflows.',
          'Rendered high-volume real-time event streams using RxJS for backpressure and state consistency.',
          'Optimized data-heavy dashboards using code splitting, lazy loading and bundle optimization.',
          'Integrated with Node.js/NestJS services and Kafka/RabbitMQ event-driven systems.',
        ],
        technologies: [
          'React',
          'Vue.js',
          'TypeScript',
          'RxJS',
          'REST APIs',
          'Node.js',
          'NestJS',
          'Kafka',
          'RabbitMQ',
        ],
        caseStudySlug: 'nationwide-disaster-alert-platform',
      },
      {
        id: 'telehealth',
        title: 'Online Healthcare Platform - Telehealth Product',
        period: { start: '2020-03', end: '2022-01', label: 'March 2020 - January 2022' },
        market: 'Israel',
        summary:
          'Next.js telehealth application for appointment scheduling and video consultations.',
        highlights: [
          'Led development of a Next.js application for appointment scheduling and video consultations.',
          'Used server-side rendering to improve initial-load performance.',
          'Established a Storybook component library for patient-facing and provider-facing modules.',
          'Architected secure patient-facing workflows for personal health data.',
          'Deployed on Azure with Docker.',
        ],
        technologies: ['Next.js', 'React', 'TypeScript', 'Storybook', 'Node.js', 'Azure', 'Docker'],
        caseStudySlug: 'telehealth-platform',
      },
      {
        id: 'booking',
        title: 'Booking Management Platform - Hospitality SaaS',
        period: { start: '2019-03', end: '2020-03', label: 'March 2019 - March 2020' },
        market: 'Germany and Switzerland',
        summary: 'Multi-tenant booking and appointment platform for barbershops and restaurants.',
        highlights: [
          'Spearheaded the React and Next.js frontend of a multi-tenant booking and appointment platform for barbershops and restaurants.',
          'Introduced Apollo Client for GraphQL data fetching and caching.',
          'Maintained a shared Storybook component library.',
        ],
        technologies: [
          'React',
          'Next.js',
          'TypeScript',
          'Apollo Client',
          'GraphQL',
          'Storybook',
          'Node.js',
        ],
        caseStudySlug: 'booking-management-saas',
      },
      {
        id: 'contracts',
        title: 'Online Contracts Management Platform for Artists',
        period: { start: '2014-10', end: '2018-10', label: 'October 2014 - October 2018' },
        market: 'Germany',
        summary: 'Single-page contracts management platform delivered by a team of 8-10 engineers.',
        highlights: [
          'Led a team of 8-10 engineers.',
          'Coordinated frontend architecture, estimation and release delivery.',
          'Architected an AngularJS and TypeScript SPA with Redux and Webpack.',
          'Mentored engineers through reviews and architecture discussions.',
          'Established frontend development standards.',
        ],
        technologies: [
          'AngularJS',
          'TypeScript',
          'Redux',
          'Webpack',
          'Node.js',
          'Express.js',
          'PostgreSQL',
          'Docker',
        ],
      },
    ],
  },
  {
    id: 'conex',
    company: 'Conex Group',
    role: 'Frontend Developer',
    location: 'Iasi, Romania',
    period: { start: '2014-03', end: '2014-10', label: 'March 2014 - October 2014' },
    summary: 'Internal portal for more than 500 employees.',
    highlights: [
      'Implemented an internal portal for more than 500 employees.',
      'Built employee collaboration and resource-allocation interfaces.',
      'Integrated PHP, MySQL, Joomla and WordPress.',
    ],
    technologies: ['PHP', 'JavaScript', 'jQuery', 'HTML5', 'CSS3', 'MySQL'],
    engagements: [],
  },
  {
    id: 'centric',
    company: 'Centric IT Solutions Romania',
    role: 'Frontend Developer',
    location: 'Iasi, Romania',
    period: { start: '2012-06', end: '2014-03', label: 'June 2012 - March 2014' },
    summary: 'Public-sector and social-care digital services for a Swiss client.',
    highlights: [
      'Modernized public-sector digital services for a Swiss client.',
      'Built citizen-facing interfaces with Sencha Touch, JavaScript and jQuery.',
      'Developed HTML5 SaaS applications for social worker-client communication.',
      'Built a nursing time-registration application with offline functionality.',
    ],
    technologies: ['Sencha Touch', 'JavaScript', 'jQuery', 'HTML5', '.NET'],
    engagements: [],
  },
  {
    id: 'freelance',
    company: 'Crismaru Constantin PFA',
    role: 'JavaScript Developer (freelance)',
    location: 'Romania',
    period: { start: '2006-04', end: '2012-07', label: 'April 2006 - July 2012' },
    summary: 'Responsive websites and custom web solutions for small and medium-sized businesses.',
    highlights: [
      'Designed and developed responsive websites and custom web solutions for small and medium-sized businesses.',
      'Integrated third-party services, analytics tools, maps and social functionality.',
      'Managed requirements, estimation, development, delivery and post-launch support directly with clients.',
    ],
    technologies: [
      'HTML5',
      'CSS3',
      'JavaScript',
      'jQuery',
      'Responsive Web Design',
      'Bootstrap',
      'Git',
    ],
    engagements: [],
  },
];
